import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, Send, Inbox, Clock, CheckCheck, Search, 
  Reply, Trash2, ChevronLeft, User, Users, Truck,
  ClipboardCheck, Wrench, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const APP_INFO = {
  ridero: { name: 'Ridero', icon: Truck },
  pti_pector: { name: 'PTI Pector', icon: ClipboardCheck },
  mechanic_kiosk: { name: 'Mechanic Kiosk', icon: Wrench }
};

export default function MessagesPage() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterApp, setFilterApp] = useState('');
  
  // Compose form
  const [composeData, setComposeData] = useState({
    recipient_email: '',
    subject: '',
    body: '',
    target_app: ''
  });
  const [replyBody, setReplyBody] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API}/api/messages/inbox`, { withCredentials: true }),
        axios.get(`${API}/api/messages/sent`, { withCredentials: true })
      ]);
      setInbox(inboxRes.data.messages || []);
      setSent(sentRes.data.messages || []);
      setUnreadCount(inboxRes.data.unread_count || 0);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (app = '', query = '') => {
    try {
      let url = `${API}/api/messages/users?`;
      if (app) url += `app_id=${app}&`;
      if (query) url += `search=${query}`;
      const res = await axios.get(url, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleOpenCompose = () => {
    setShowCompose(true);
    setComposeData({ recipient_email: '', subject: '', body: '', target_app: '' });
    fetchUsers();
  };

  const handleSendMessage = async () => {
    if (!composeData.recipient_email || !composeData.subject || !composeData.body) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/api/messages`, composeData, { withCredentials: true });
      toast.success('Message sent!');
      setShowCompose(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleViewMessage = async (message) => {
    try {
      const res = await axios.get(`${API}/api/messages/${message.message_id}`, { withCredentials: true });
      setSelectedMessage(res.data);
      setReplyBody('');
      // Refresh to update read status
      fetchMessages();
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const handleReply = async () => {
    if (!replyBody.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await axios.post(`${API}/api/messages/${selectedMessage.message_id}/reply`, {
        body: replyBody
      }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyBody('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/api/messages/${messageId}`, { withCredentials: true });
      toast.success('Message deleted');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/api/messages/mark-read`, {}, { withCredentials: true });
      toast.success('All messages marked as read');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to mark messages as read');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff/86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Mail className="w-8 h-8" />
              Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">{unreadCount} new</Badge>
              )}
            </h1>
            <p className="text-muted-foreground mt-1">
              Send messages to users across all connected apps
            </p>
          </div>
          <Button onClick={handleOpenCompose} data-testid="compose-btn">
            <Send className="w-4 h-4 mr-2" />
            Compose
          </Button>
        </div>

        {/* Message View */}
        {selectedMessage ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setSelectedMessage(null)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(selectedMessage.message_id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-b pb-4">
                <h2 className="text-xl font-bold">{selectedMessage.subject}</h2>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedMessage.sender_id === user?.user_id ? 'You' : selectedMessage.sender_name}
                  </span>
                  <span>→</span>
                  <span>{selectedMessage.recipient_id === user?.user_id ? 'You' : selectedMessage.recipient_name}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(selectedMessage.created_at)}
                  </span>
                  {selectedMessage.target_app && APP_INFO[selectedMessage.target_app] && (
                    <Badge variant="outline">
                      {APP_INFO[selectedMessage.target_app].name}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="py-4 whitespace-pre-wrap min-h-[200px]">
                {selectedMessage.body}
              </div>
              
              {/* Reply section - only for received messages */}
              {selectedMessage.recipient_id === user?.user_id && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Reply</Label>
                  <Textarea
                    placeholder="Write your reply..."
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    rows={4}
                  />
                  <Button onClick={handleReply}>
                    <Reply className="w-4 h-4 mr-2" />
                    Send Reply
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Message List */
          <Tabs defaultValue="inbox">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="inbox" className="flex items-center gap-2">
                  <Inbox className="w-4 h-4" />
                  Inbox
                  {unreadCount > 0 && (
                    <Badge variant="secondary" className="ml-1">{unreadCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="sent" className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Sent
                </TabsTrigger>
              </TabsList>
              
              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>

            <TabsContent value="inbox" className="mt-4">
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                  ) : inbox.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No messages yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {inbox.map(msg => (
                        <div 
                          key={msg.message_id}
                          className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${!msg.is_read ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                          onClick={() => handleViewMessage(msg)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {!msg.is_read && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                                <span className={`font-medium ${!msg.is_read ? 'font-bold' : ''}`}>
                                  {msg.sender_name || msg.sender_email}
                                </span>
                                {msg.target_app && APP_INFO[msg.target_app] && (
                                  <Badge variant="outline" className="text-xs">
                                    {APP_INFO[msg.target_app].name}
                                  </Badge>
                                )}
                              </div>
                              <p className={`text-sm ${!msg.is_read ? 'font-semibold' : ''}`}>
                                {msg.subject}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">
                                {msg.body.substring(0, 100)}...
                              </p>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sent" className="mt-4">
              <Card>
                <CardContent className="p-0">
                  {sent.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Send className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No sent messages</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {sent.map(msg => (
                        <div 
                          key={msg.message_id}
                          className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleViewMessage(msg)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  To: {msg.recipient_name || msg.recipient_email}
                                </span>
                                {msg.is_read && (
                                  <CheckCheck className="w-4 h-4 text-green-500" title="Read" />
                                )}
                              </div>
                              <p className="text-sm">{msg.subject}</p>
                              <p className="text-sm text-muted-foreground truncate">
                                {msg.body.substring(0, 100)}...
                              </p>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Compose Dialog */}
        <Dialog open={showCompose} onOpenChange={setShowCompose}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                New Message
              </DialogTitle>
              <DialogDescription>
                Send a message to any user in your connected apps
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Filter by app */}
              <div className="flex gap-2">
                <Select value={filterApp} onValueChange={(v) => { setFilterApp(v); fetchUsers(v, searchQuery); }}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by app" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Apps</SelectItem>
                    <SelectItem value="ridero">Ridero</SelectItem>
                    <SelectItem value="pti_pector">PTI Pector</SelectItem>
                    <SelectItem value="mechanic_kiosk">Mechanic Kiosk</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); fetchUsers(filterApp, e.target.value); }}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Recipient */}
              <div className="space-y-2">
                <Label>To</Label>
                {composeData.recipient_email ? (
                  <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                    <User className="w-4 h-4" />
                    <span>{composeData.recipient_email}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="ml-auto h-6 w-6"
                      onClick={() => setComposeData({...composeData, recipient_email: ''})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <p className="p-3 text-sm text-muted-foreground">No users found</p>
                    ) : (
                      filteredUsers.map(u => (
                        <div 
                          key={u.user_id}
                          className="p-2 hover:bg-muted cursor-pointer flex items-center gap-2"
                          onClick={() => setComposeData({...composeData, recipient_email: u.email, target_app: u.connected_apps?.[0]})}
                        >
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">{u.name || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                          {u.connected_apps?.length > 0 && (
                            <Badge variant="outline" className="ml-auto text-xs">
                              {u.connected_apps[0]}
                            </Badge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  placeholder="Message subject"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                />
              </div>

              {/* Body */}
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  placeholder="Write your message..."
                  value={composeData.body}
                  onChange={(e) => setComposeData({...composeData, body: e.target.value})}
                  rows={6}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompose(false)}>Cancel</Button>
              <Button onClick={handleSendMessage}>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
